/**
 * Maps AWS-account-style selector ids to canonical company tenant ids (X-Kg-Tenant-Id / GLOBAL# prefs).
 *
 * After Phase 3 Dynamo migration, EA + team prefs live under companyTenantId (a generated
 * `company_<32-hex>` value), while the header may still list connected AWS accounts by
 * account row id (12-digit account, slug, etc.).
 *
 * On production hosts the runtime trusts only `/signup/status` for tenant membership;
 * env-baked maps are unconditionally ignored (see `getCompanyTenantMap`). The legacy
 * `company_staging_*` ids are reserved for non-production environments and are actively
 * rejected if they appear on a production host (see `isForbiddenTenantOnCurrentHost`).
 *
 * Priority on non-prod: account.companyTenantId (from API) > REACT_APP_COMPANY_TENANT_MAP > passthrough.
 */

// Forbidden on production hosts. Any tenant id starting with this prefix
// (e.g. company_staging_001, company_staging_blue, company_staging_test) is
// considered staging-only and refused if encountered while the SPA is loaded
// from the canonical production host. The 2026-05-06 outage was triggered by
// `company_staging_001` slipping into the prod bundle via
// REACT_APP_COMPANY_TENANT_MAP — this prefix-based check covers any future
// variant by construction.
export const STAGING_TENANT_PREFIX = 'company_staging_';

// Internal regex: prefix match with optional underscore-or-hyphen separator
// (handles hand-typed variants like `company_staging-002`). Case-insensitive.
const STAGING_TENANT_PREFIX_RE = /^company_staging[_-]/i;

// CRA DefinePlugin replaces process.env.REACT_APP_* at compile time with literal strings.
// IMPORTANT: Do NOT wrap these in typeof/process guards — CRA 5 does not polyfill `process`,
// so runtime `typeof process !== 'undefined'` is false and the DefinePlugin-inserted literal
// is never read. Module-scope access is safe because the replacement happens before execution.
const _SINGLE_TENANT_ID = String(process.env.REACT_APP_SINGLE_COMPANY_TENANT_ID || '').trim();
const _TENANT_MAP_RAW = String(process.env.REACT_APP_COMPANY_TENANT_MAP || '');
const _DISPLAY_NAMES_RAW = String(process.env.REACT_APP_COMPANY_DISPLAY_NAMES || '');

function safeParseMap(raw) {
  if (!raw || typeof raw !== 'string') return {};
  try {
    const o = JSON.parse(raw);
    return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
  } catch {
    return {};
  }
}

export function getCompanyTenantMap() {
  // Production host must NEVER use a baked tenant map. The .env-cmdrc.example
  // file historically shipped staging tenant ids in the production profile,
  // and a fresh `cp .env-cmdrc.example .env-cmdrc` before a production build
  // poisons the bundle with `company_staging_001`. Strip it at runtime.
  if (typeof window !== 'undefined' && window.location) {
    if (isProductionHostname(String(window.location.hostname || '').toLowerCase())) {
      return {};
    }
  }
  return safeParseMap(_TENANT_MAP_RAW);
}

/**
 * Implicit default company tenant for API/prefs when the host is "staging-like".
 * - Deployed staging: staging.krossglobalmarketing.com
 * - Any browser loopback (localhost / 127.0.0.1 / ::1): always company_staging_001.
 *
 * Important: a **production** webpack build (`npm run build`) served on localhost has
 * NODE_ENV=production and REACT_APP_ENV=production; production .env-cmdrc has no tenant map.
 * Without this loopback rule, workspaces split on raw AWS ids and Unibox domain-filtering hides
 * mail-staging inboxes (mock/API domains ≠ mail-staging.krossglobalmarketing.com).
 * Real production/staging deploys never use loopback hostnames.
 */
/** True for CRA dev / local browser origins (including IPv6 loopback). */
export function isLocalLoopbackHostname(hostnameLower) {
  const h = String(hostnameLower || '').toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '::1' || h === '[::1]';
}

/** True only for the canonical production host(s). Used to reject staging tenant
 *  ids that get baked into the bundle by mistake (.env-cmdrc.example regression). */
export function isProductionHostname(hostnameLower) {
  const h = String(hostnameLower || '').toLowerCase();
  return h === 'krossglobalmarketing.com' || h === 'www.krossglobalmarketing.com';
}

/** True if `tenantId` is a staging-only id (any `company_staging_*` variant).
 *  Pure helper — does not look at hostname. Use for diagnostics or test guards. */
export function isStagingTenantId(tenantId) {
  const tid = String(tenantId || '').trim();
  if (!tid) return false;
  return STAGING_TENANT_PREFIX_RE.test(tid);
}

/** Hard rule: staging tenant ids must never be applied while the SPA is loaded
 *  from the production host, regardless of localStorage / env / Auth0 metadata.
 *  Returns true if the given tenant id should be refused on the current page. */
export function isForbiddenTenantOnCurrentHost(tenantId) {
  if (typeof window === 'undefined' || !window.location) return false;
  if (!isStagingTenantId(tenantId)) return false;
  const h = String(window.location.hostname || '').toLowerCase();
  return isProductionHostname(h);
}

export function getStagingHostnameDefaultTenantId() {
  if (typeof window === 'undefined') return '';
  const h = String(window.location?.hostname || '').toLowerCase();
  if (h === 'staging.krossglobalmarketing.com') return 'company_staging_001';
  if (isLocalLoopbackHostname(h)) return 'company_staging_001';
  return '';
}

/**
 * Explicit tenant for RTK `useGetUserPreferencesQuery({ tenantId })` args.
 * Never rely on `undefined` + URL-only tenant: cache key stays `undefined` while the URL changes,
 * so the first (wrong) response can stick after context hydrates.
 */
export function resolveTenantIdForPreferencesQuery(selectedCompanyTenantId, activeTenantId) {
  const ws = String(selectedCompanyTenantId || '').trim();
  if (ws && ws.toLowerCase() !== 'all') return ws;
  const act = String(activeTenantId || '').trim();
  if (act && act.toLowerCase() !== 'all') return act;
  const hostTid = getStagingHostnameDefaultTenantId();
  return hostTid || null;
}

/** The env-var-based single-tenant override. No hostname fallback — only the build-time constant.
 *  Disabled when the user has a provisioned tenant in localStorage (from onboarding).
 *  ALWAYS empty on the production host — the env override exists only for staging/dev,
 *  and a `company_staging_*` value is also rejected on the production host even if the
 *  env var was somehow set there. */
export function getSingleCompanyTenantIdOverride() {
  const envDefault = _SINGLE_TENANT_ID;
  if (!envDefault || envDefault.toLowerCase() === 'all') {
    console.log('[TENANT-TRACE] getSingleCompanyTenantIdOverride: no env default, returning empty');
    return '';
  }
  if (isForbiddenTenantOnCurrentHost(envDefault)) {
    console.warn(
      '[TENANT-GUARDRAIL] refusing baked single-tenant override on production host (matches forbidden prefix "%s"):',
      STAGING_TENANT_PREFIX,
      envDefault
    );
    return '';
  }
  try {
    const saved = String(localStorage.getItem('selectedCompanyTenantId') || '').trim();
    console.log('[TENANT-TRACE] getSingleCompanyTenantIdOverride:', { saved, envDefault });
    if (saved && saved.startsWith('company_') && saved !== envDefault) {
      console.log('[TENANT-TRACE] -> returning EMPTY (provisioned tenant overrides default)');
      return '';
    }
  } catch (_) { /* SSR / no localStorage */ }
  console.log('[TENANT-TRACE] -> returning env override:', envDefault);
  return envDefault;
}

/**
 * @param {string} accountId - AWS accounts table id (12-digit account, slug, etc.)
 * @returns {string} canonical tenant id for API calls
 */
export function resolveCompanyTenantIdFromAccountId(accountId) {
  const single = getSingleCompanyTenantIdOverride();
  if (single) return single;
  const id = String(accountId || '').trim();
  if (!id || id.toLowerCase() === 'all') return '';
  const map = getCompanyTenantMap();
  if (map[id]) return String(map[id]).trim();
  return id;
}

/**
 * @param {Record<string, any> | null | undefined} account - row from /aws-accounts
 * @returns {string}
 */
export function resolveCompanyTenantIdFromAccount(account) {
  const single = getSingleCompanyTenantIdOverride();
  if (single) return single;
  if (!account || typeof account !== 'object') return '';
  const id = String(account.id || '').trim();
  const map = getCompanyTenantMap();
  // Map wins over API companyTenantId so mis-tagged AWS rows (e.g. BlueOak) still roll up to KrossKast.
  if (id && map[id]) return String(map[id]).trim();
  const explicit = String(account.companyTenantId || '').trim();
  if (explicit) return explicit;
  return resolveCompanyTenantIdFromAccountId(account.id);
}

/**
 * @param {string} raw - value from localStorage or URL (may be legacy AWS id or already company id)
 * @param {Record<string, any> | null} accountIfKnown - matching account from context, if any
 * @returns {string}
 */
export function resolveCompanyTenantIdFromRaw(raw, accountIfKnown) {
  const s = String(raw || '').trim();
  if (!s || s.toLowerCase() === 'all') return '';
  if (accountIfKnown && typeof accountIfKnown === 'object') {
    return resolveCompanyTenantIdFromAccount(accountIfKnown);
  }
  return resolveCompanyTenantIdFromAccountId(s);
}

/** UI label for header / workspace selector (REACT_APP_COMPANY_DISPLAY_NAMES JSON). */
export function getCompanyDisplayName(companyTenantId) {
  const id = String(companyTenantId || '').trim();
  if (!id) return '';
  // On production host, refuse to render a friendly label for ANY staging
  // tenant id — that label was the visual confirmation of the cross-environment
  // leak that caused the 2026-05-06 outage. The check is prefix-based so it
  // covers any future company_staging_* variant by construction.
  const isProdHost = typeof window !== 'undefined'
    && window.location
    && isProductionHostname(String(window.location.hostname || '').toLowerCase());
  if (isProdHost && isStagingTenantId(id)) return '';
  const names = isProdHost ? {} : safeParseMap(_DISPLAY_NAMES_RAW);
  const fromMap = String(names[id] || '').trim();
  if (fromMap) return fromMap;
  // Legacy canonical org label kept only on non-production hosts (dev / staging).
  if (!isProdHost && id === 'company_staging_001') return 'KrossKast';
  return '';
}

/** Legacy dev mock main-account id — never show raw in the navbar when maps collapse to company tenant. */
export const LEGACY_KROSSGLOBAL_MOCK_ACCOUNT_ID = '982423662433';

/**
 * Single workspace row label: env display name, else one linked account alias, else last-resort human text.
 * @param {string} companyTenantId
 * @param {Array<Record<string, unknown>> | undefined} awsAccounts
 */
export function formatWorkspaceDisplayName(companyTenantId, awsAccounts) {
  const ct = String(companyTenantId || '').trim();
  if (!ct) return '';
  const fromEnv = getCompanyDisplayName(ct);
  if (fromEnv) return fromEnv;
  const accounts = Array.isArray(awsAccounts) ? awsAccounts : [];
  if (accounts.length === 1) {
    const a = accounts[0];
    const label = String((a && (a.alias || a.name)) || '').trim();
    if (label) return label;
  }
  if (ct === LEGACY_KROSSGLOBAL_MOCK_ACCOUNT_ID) return 'KrossGlobal';
  return ct;
}

/** Raw AWS account row ids (12 digits) must not be used as company workspace ids on loopback. */
const RAW_AWS_ACCOUNT_ID = /^\d{12}$/;

let loopbackParityViolationLogged = false;

/**
 * Dev guardrail: on browser loopback, company workspace state must stay on the canonical company tenant.
 * Logs once per page load if we would regress to multi-workspace / raw AWS ids (silent staging drift).
 *
 * Opt out (intentional multi-company local testing): set REACT_APP_LOCALHOST_MULTI_COMPANY=true
 */
export function reportLoopbackTenantParityViolations(companyWorkspaces) {
  if (typeof window === 'undefined') return;
  if (process.env.REACT_APP_LOCALHOST_MULTI_COMPANY === 'true') return;
  const h = String(window.location?.hostname || '').toLowerCase();
  if (!isLocalLoopbackHostname(h)) return;

  const workspaces = Array.isArray(companyWorkspaces) ? companyWorkspaces : [];
  if (workspaces.length === 0) return;

  const single = getSingleCompanyTenantIdOverride();
  const issues = [];
  if (!single) issues.push('missing_single_company_tenant_override');
  if (workspaces.length > 1) issues.push('multiple_company_workspaces');
  for (const w of workspaces) {
    const ct = String(w?.companyTenantId || '').trim();
    if (RAW_AWS_ACCOUNT_ID.test(ct)) issues.push(`raw_aws_workspace_id:${ct}`);
  }
  if (single && workspaces.some((w) => String(w?.companyTenantId || '').trim() !== single)) {
    issues.push('workspace_tenant_mismatch');
  }

  if (issues.length === 0) return;
  if (loopbackParityViolationLogged) return;
  loopbackParityViolationLogged = true;
  console.error('[KG localhost parity guard] Loopback must not fall back to raw AWS tenant behavior.', {
    issues,
    singleOverride: single || null,
    workspaceIds: workspaces.map((w) => w.companyTenantId),
    hint: 'See getStagingHostnameDefaultTenantId / REACT_APP_SINGLE_COMPANY_TENANT_ID, or set REACT_APP_LOCALHOST_MULTI_COMPANY=true to opt out.',
  });
}
